import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "disabled", "focused", "onFocus", "onBlur", "id", "name", "checked", "onChange", "defaultChecked", "onClick", "readOnly", "size", "textChecked", "textUnchecked", "use"];
import I18n from 'I18n';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { BATTLESHIP, CALYPSO, GYPSUM, HEFFALUMP, KOALA, OLAF, SLINKY, BUTTON_DISABLED_TEXT } from 'HubStyleTokens/colors';
import UICheckmark from '../icon/icons/UICheckmark';
import lazyEval from '../utils/lazyEval';
import memoWithDisplayName from '../utils/memoWithDisplayName';
import { setBorderRadius, setUiTransition, uiFocus, FONT_FAMILIES, setFontSmoothing } from '../utils/Styles';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { hidden } from '../utils/propTypes/decorators';
import useFocused from '../hooks/useFocused';
import { useChecked, checkedPropTypes, checkedDefaultProps } from '../hooks/useChecked';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultProps = Object.assign({}, checkedDefaultProps, {
  size: 'default',
  textChecked: () => I18n.text('salesUI.UIToggle.checkedLabel'),
  textUnchecked: () => I18n.text('salesUI.UIToggle.uncheckedLabel'),
  use: 'default'
});
const SIZE_VALUES = {
  default: 40,
  sm: 32,
  xs: 26
};
const Outer = styled.label.withConfig({
  displayName: "UIToggle__Outer",
  componentId: "sc-1j7vnkm-0"
})(["position:relative;display:inline-block;vertical-align:middle;user-select:none;color:", ";cursor:", ";"], ({
  isOnDark
}) => isOnDark && BATTLESHIP, ({
  disabledOrReadOnly
}) => disabledOrReadOnly ? 'not-allowed' : 'pointer');
const Switch = styled.span.withConfig({
  displayName: "UIToggle__Switch",
  componentId: "sc-1j7vnkm-1"
})(["position:relative;display:inline-block;vertical-align:middle;height:", "px;width:", ";min-width:", "px;background-color:", ";box-shadow:", ";", ";.hubspot-enable-focus-styles &{", ";}"], ({
  size = defaultProps.size
}) => SIZE_VALUES[size], ({
  size = defaultProps.size
}) => size !== 'default' && `${SIZE_VALUES[size] * 2}px`, ({
  size
}) => size === 'default' ? 80 : 0, ({
  checked,
  disabledOrReadOnly,
  isOnDark,
  size
}) => {
  if (disabledOrReadOnly) return GYPSUM;
  if (checked) return CALYPSO;
  if (isOnDark) return HEFFALUMP;
  return size === 'default' ? KOALA : GYPSUM;
}, ({
  checked,
  disabledOrReadOnly,
  isOnDark
}) => (!checked || disabledOrReadOnly) && `inset 0 0 0 1px ${isOnDark ? SLINKY : BATTLESHIP}`, setBorderRadius(), ({
  focused
}) => focused && uiFocus);
const Handle = styled.span.withConfig({
  displayName: "UIToggle__Handle",
  componentId: "sc-1j7vnkm-2"
})(["position:absolute;top:0;left:", ";will-change:left;height:", "px;width:", "px;padding:", "px;overflow:hidden;background-color:", ";box-shadow:", ";", ";", ";"], ({
  checked,
  size = defaultProps.size
}) => checked ? `calc(100% - (${SIZE_VALUES[size]}px))` : 0, ({
  size = defaultProps.size
}) => SIZE_VALUES[size], ({
  size = defaultProps.size
}) => SIZE_VALUES[size], ({
  size
}) => size === 'xs' ? 5 : 7, ({
  disabledOrReadOnly
}) => disabledOrReadOnly ? GYPSUM : OLAF, ({
  checked,
  disabledOrReadOnly,
  isOnDark
}) => {
  let boxShadowColor;
  if (disabledOrReadOnly) {
    boxShadowColor = BATTLESHIP;
  } else if (checked) {
    boxShadowColor = CALYPSO;
  } else {
    boxShadowColor = isOnDark ? SLINKY : BATTLESHIP;
  }
  return `0 0 0 1px ${boxShadowColor}, 0 5px 5px 0 rgba(0, 0, 0, 0.08)`;
}, setBorderRadius(), setUiTransition());
const Text = styled.span.withConfig({
  displayName: "UIToggle__Text",
  componentId: "sc-1j7vnkm-3"
})(["position:relative;display:block;top:", ";", ";text-align:center;line-height:41px;padding-left:", "px;padding-right:", "px;visibility:", ";color:", ";", ";"], ({
  checked,
  size
}) => size === 'default' && !checked && '-40px', FONT_FAMILIES.demibold, ({
  checked
}) => checked ? 16 : 56, ({
  checked
}) => checked ? 56 : 16, ({
  visible
}) => !visible && 'hidden', ({
  checked,
  disabledOrReadOnly
}) => {
  if (disabledOrReadOnly) return BUTTON_DISABLED_TEXT;
  if (checked) return OLAF;
  return null;
}, setFontSmoothing());
const Checkmark = styled(({
  className
}) => /*#__PURE__*/_jsx(UICheckmark, {
  className: className
})).withConfig({
  displayName: "UIToggle__Checkmark",
  componentId: "sc-1j7vnkm-4"
})(["fill:", ";stroke:", ";stroke-width:2;"], ({
  disabledOrReadOnly
}) => disabledOrReadOnly ? BUTTON_DISABLED_TEXT : CALYPSO, ({
  disabledOrReadOnly
}) => disabledOrReadOnly ? GYPSUM : OLAF);
const UIToggle = memoWithDisplayName('UIToggle', props => {
  const {
      className,
      disabled,
      id,
      name,
      defaultChecked: __defaultChecked = defaultProps.defaultChecked,
      onClick,
      readOnly,
      size = defaultProps.size,
      textChecked = defaultProps.textChecked,
      textUnchecked = defaultProps.textUnchecked,
      use = defaultProps.use
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    checked,
    onChange
  } = useChecked(props);
  const disabledOrReadOnly = disabled || readOnly;
  const labelClassName = classNames("uiToggle private-form__toggle-switch", className, disabledOrReadOnly && 'private-form__toggle-switch--disabled');
  const switchClassName = "uiToggleSwitch private-form__toggle-switch__inner" + (checked ? " uiToggleSwitchOn" : "");
  const {
    focused,
    onFocus,
    onBlur
  } = useFocused(props);
  const shorthandSize = size !== 'default' ? toShorthandSize(size) : size;
  const isOnDark = use === 'on-dark';
  const handleChange = evt => {
    if (disabledOrReadOnly || !onChange) {
      evt.preventDefault();
      return;
    }
    onChange(evt);
  };
  return /*#__PURE__*/_jsxs(Outer, Object.assign({}, rest, {
    className: labelClassName,
    disabledOrReadOnly: disabledOrReadOnly,
    isOnDark: isOnDark,
    size: shorthandSize,
    onFocus: onFocus,
    onBlur: onBlur,
    children: [/*#__PURE__*/_jsx("input", {
      checked: checked,
      disabled: disabled,
      className: "private-form__toggle-input sr-only",
      id: id,
      name: name,
      onChange: handleChange,
      onClick: onClick,
      readOnly: readOnly,
      type: "checkbox"
    }), /*#__PURE__*/_jsxs(Switch, {
      checked: checked,
      className: switchClassName,
      disabledOrReadOnly: disabledOrReadOnly,
      focused: focused,
      role: "presentation",
      size: shorthandSize,
      isOnDark: isOnDark,
      children: [/*#__PURE__*/_jsx(Text, {
        checked: true,
        disabledOrReadOnly: disabledOrReadOnly,
        size: shorthandSize,
        visible: checked,
        className: shorthandSize !== 'default' ? 'sr-only' : undefined,
        children: lazyEval(textChecked)
      }), /*#__PURE__*/_jsx(Text, {
        checked: false,
        disabledOrReadOnly: disabledOrReadOnly,
        size: shorthandSize,
        visible: !checked,
        className: 'private-form__toggle-switch__label--unchecked' + (shorthandSize !== 'default' ? " sr-only" : ""),
        children: lazyEval(textUnchecked)
      }), /*#__PURE__*/_jsx(Handle, {
        checked: checked,
        disabledOrReadOnly: disabledOrReadOnly,
        isOnDark: isOnDark,
        size: shorthandSize,
        children: checked && /*#__PURE__*/_jsx(Checkmark, {
          disabledOrReadOnly: disabledOrReadOnly
        })
      })]
    })]
  }));
});
UIToggle.propTypes = Object.assign({}, checkedPropTypes, {
  disabled: PropTypes.bool,
  focused: hidden(PropTypes.bool),
  name: PropTypes.string,
  readOnly: PropTypes.bool,
  size: PropTypes.oneOfType([propTypeForSizes(['xs', 'sm']), PropTypes.oneOf(['default'])]).isRequired,
  textChecked: createLazyPropType(PropTypes.node).isRequired,
  textUnchecked: createLazyPropType(PropTypes.node).isRequired,
  use: PropTypes.oneOf(['default', 'on-dark'])
});
UIToggle.defaultProps = defaultProps;
export default UIToggle;